import React from 'react';
import {makeStyles} from "@mui/styles";

// MUI Imports
import {
    Chip,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

// My Imports
import HappyBox from "../layout/HappyBox";

import {colorStyles} from "../../constants/colorStyles";

const useStyles = makeStyles({
    root: {
        padding: '20px',
        marginTop: '20px',
        marginBottom: '50px'
    },
    title: {
        marginBottom: '20px'
    },
    basicsContainer: {
        marginBottom: '20px',
        '& p': {
            whiteSpace: 'pre-wrap',
            marginBottom: '20px'
        }
    },
    ...colorStyles
});

const ReadinessChangeResults = (props) => {
    const classes = useStyles();
    const {results} = props;
    let existTie = false;
    let previousScore = null;

    return (
        <>
            <HappyBox>
                <Typography variant='h4' gutterBottom>
                    Readiness for Change
                </Typography>

                <Typography>
                    <b>What is it?</b>{" "}
                </Typography>
                <Typography>
                    The Readiness for Change Scale is a psychological assessment tool used to measure a person’s
                    willingness, motivation, and preparedness to make a behavioral and/or life change.
                </Typography>
                <br></br>
                <Typography>
                    <b>Why is it used?</b>{" "}
                </Typography>
                <Typography>
                    We use this test to assess your readiness for a career change. Some individuals are more
                    prepared than others, and understanding where you are helps us tailor our approach to
                    interpreting the results.
                </Typography>
            </HappyBox>

            <HappyBox>
                <Typography variant='h4' gutterBottom>
                    Your Results
                </Typography>
                <div>
                    <Typography variant='h5'>
                        Stage: <b>{results.stage}</b>
                    </Typography>
                </div>
                <br/>

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Stage</TableCell>
                                <TableCell>Score</TableCell>
                                <TableCell>Score</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {results.scores.map(score => {
                                if (!existTie && previousScore === score.score)
                                    existTie = true;
                                previousScore = score.score;
                                return (
                                    <TableRow key={score.stage}>
                                        <TableCell>
                                            <Chip label={score.stage}
                                                  className={classes[score.stage.toLowerCase()]}/>
                                        </TableCell>
                                        <TableCell>
                                            <LinearProgress variant="determinate" value={(score.score * 100 / 8)}
                                                            className={classes.bar}/>
                                        </TableCell>
                                        <TableCell>{score.score}</TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <br/>
                {existTie && <Typography>
                    In the case of a tie between two scores, we interpret the latter score as the direction you are
                    moving toward.
                    <br/>
                    In this case, your latter score is: <b>{results.stage}</b>
                    <br/><br/>
                </Typography>}
                {results.stage === 'Precontemplation' && <div>
                    <Typography>
                        Your scores indicate that you are in a stage of precontemplation, meaning that you're
                        feeling uncertain or hesitant about the idea of making a career change. Perhaps you are not
                        sure if you want or need to make a career or job change, or that change is even possible for
                        you. That’s completely ok! Everyone approaches change differently, and sometimes the first
                        step is just becoming more aware of how you feel, understanding what’s working with your
                        current career, and what’s not. You don’t need to commit to any big decisions right now, but
                        it might be helpful to start reflecting on questions like:
                    </Typography>
                    <ul>
                        <li>What do I enjoy (or not enjoy) about my current role?</li>
                        <li>If nothing changed in the next year, how would I feel?</li>
                        <li>What would I be curious to explore, even just a little?</li>
                    </ul>
                    <Typography>
                        Our goal isn’t to rush you, but to give you tools and insights to make a confident decision
                        when (and if) you're ready. Perhaps you could consider, just as a hypothetical, what a
                        career change might look like: Would it make a difference to your happiness? Your income? Do
                        any of the suggested career pathways sound interesting?
                    </Typography>
                </div>}

                {results.stage === 'Contemplation' && <div>
                    <Typography>
                        Your score indicates that you are in a stage of contemplation, meaning that you’re starting
                        to think seriously about a possible career change, but you may feel unsure about what
                        direction to take, or whether making a change is the right move for you. That’s completely
                        normal! This stage is often about holding both sides: a desire for something different and
                        the comfort (or fear) of staying where you are.
                    </Typography>
                    <br/>
                    <Typography>
                        Rather than rushing into a decision, this is a great time to make a list of pros and cons,
                        and compare them, You might also pay close attention to the results of the Values Card Sort
                        to compare your pro/con list with your career values. Do the pros move you closer to having
                        a career that is in line with your values? Your Career Coach can help you consider what
                        steps you might need to take to begin making change, and help you navigate any obstacles you
                        are concerned about clearing.
                    </Typography>
                    <br/>
                    <Typography>
                        You might also want to explore questions like:
                    </Typography>
                    <ul>
                        <li>What feels like it’s no longer working for me?</li>
                        <li>What would a more fulfilling career look like?</li>
                        <li>What am I afraid I might lose by making a change, and what might I gain?</li>
                    </ul>
                    <Typography>
                        You don’t need all the answers yet. The goal right now is to clarify what matters to you,
                        build insight, and reduce the fear of the unknown before making a career decision.
                    </Typography>
                </div>}

                {results.stage === 'Action' && <div>
                    <Typography>
                        Your score indicates that you are in a stage of action, meaning that you are already taking
                        steps towards a career change (engaging with Wanderlust was step one–that’s a big deal, and
                        it takes courage to move from thinking to doing!) At this stage, the focus is on clarifying
                        your direction, building momentum, and staying resilient as you navigate the ups and downs
                        of
                        change. Some days will feel energizing, others might feel frustrating or uncertain, and
                        that’s all
                        part of the process.
                    </Typography>
                    <br/>
                    <Typography>

                        You are ready to be proactive, to take more actionable steps towards change, and to work
                        with a Career Coach to keep the momentum going. Your Career Coach will support you with all
                        the
                        practical tools, strategy, and guidance you need so you can stay focused and make confident
                        choices. If there are specific roadblocks or questions coming up, we will tackle them
                        together. You are well on your way!
                    </Typography>
                </div>}
            </HappyBox>

        </>
    )
}

export default ReadinessChangeResults
